import React from "react";
import { motion } from "framer-motion";
import { Heart, Sparkles, Wind, Sun, ArrowRight, Linkedin, Instagram } from "lucide-react";
import { whatsappLink } from "@/config/site";

const pillars = [
  { icon: Heart, t: "Gestão Emocional", d: "Compreender e regular emoções, com ferramentas práticas para o dia-a-dia." },
  { icon: Wind, t: "Gestão de Stress & Ansiedade", d: "Técnicas para gerir a pressão escolar, social e pessoal." },
  { icon: Sun, t: "Autoestima & Confiança", d: "Construir uma relação saudável consigo próprio e com os outros." },
  { icon: Sparkles, t: "Coaching em PNL", d: "Programação Neurolinguística aplicada ao desempenho, à expressão e ao crescimento pessoal." },
];

const coachUrls = {
  linkedin: "https://www.linkedin.com/in/madalena-guedes-coach-453728411/",
  instagram: "https://www.instagram.com/p/DYH3L9gCPcw/?img_index=2",
};

export const Wellbeing = () => {
  return (
    <section id="wellbeing" data-testid="wellbeing-section" className="relative section overflow-hidden">
      {/* Soft gradient background */}
      <div aria-hidden className="absolute inset-0 bg-gradient-to-br from-bordeaux-50/70 via-stone-50 to-navy-50/40" />
      <div aria-hidden className="absolute -top-32 -left-32 h-[28rem] w-[28rem] rounded-full bg-bordeaux-100/60 blur-3xl" />
      <div aria-hidden className="absolute -bottom-32 -right-32 h-[28rem] w-[28rem] rounded-full bg-navy-100/50 blur-3xl" />

      <div className="container-tight relative">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-6"
          >
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white border border-bordeaux-100 text-bordeaux-700 text-xs font-semibold uppercase tracking-[0.22em] mb-6">
              <Heart size={14} /> Pilar do iLg
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-stone-900 leading-[1.05]">
              Wellbeing <span className="italic text-bordeaux-700">no centro</span> de tudo.
            </h2>
            <p className="mt-6 text-stone-700 leading-relaxed text-lg max-w-xl">
              No iLg, o bem-estar não é um extra — é a base. Acreditamos que um aluno equilibrado aprende mais,
              decide melhor e cresce com confiança. Por isso integramos o coaching em PNL e o desenvolvimento
              pessoal em todos os percursos.
            </p>

            <div className="mt-9 grid sm:grid-cols-2 gap-4">
              {pillars.map((p, i) => (
                <motion.div
                  key={p.t}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.07 }}
                  data-testid={`wellbeing-pillar-${i}`}
                  className="p-5 rounded-2xl bg-white/80 backdrop-blur border border-white hover:border-bordeaux-200 hover:bg-white transition-all hover:-translate-y-0.5"
                >
                  <div className="h-10 w-10 rounded-xl bg-bordeaux-50 text-bordeaux-700 flex items-center justify-center">
                    <p.icon size={18} strokeWidth={1.5} />
                  </div>
                  <div className="mt-3 font-display text-lg text-stone-900">{p.t}</div>
                  <p className="mt-1 text-sm text-stone-600 leading-relaxed">{p.d}</p>
                </motion.div>
              ))}
            </div>

            <a
              href={whatsappLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="wellbeing-cta-btn"
              className="mt-10 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-bordeaux-700 text-white font-semibold hover:bg-bordeaux-800 transition-all hover:-translate-y-0.5 shadow-[0_10px_30px_rgba(124,31,44,0.22)]"
            >
              Saber mais sobre wellbeing <ArrowRight size={18} />
            </a>
          </motion.div>

          {/* Coach highlight card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-6 relative"
          >
            <div className="relative rounded-[2rem] bg-white border border-stone-100 shadow-[0_30px_80px_rgba(28,25,23,0.10)] overflow-hidden">
              {/* Decorative top */}
              <div className="relative h-44 sm:h-52 bg-gradient-to-br from-bordeaux-700 via-bordeaux-600 to-navy-700 overflow-hidden">
                <div aria-hidden className="absolute inset-0 opacity-20" style={{
                  backgroundImage: 'radial-gradient(circle at 25% 30%, white 1px, transparent 1px)',
                  backgroundSize: '24px 24px',
                }} />
                <div className="absolute top-5 left-5">
                  <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/95 backdrop-blur text-bordeaux-700 text-[10px] uppercase tracking-[0.22em] font-bold">
                    Responsável pelo Coaching
                  </span>
                </div>
                {/* Avatar */}
                <div className="absolute -bottom-12 left-8 sm:left-10">
                  <div className="h-24 w-24 sm:h-28 sm:w-28 rounded-full bg-white ring-4 ring-white shadow-xl flex items-center justify-center">
                    <span className="font-display text-4xl sm:text-5xl text-bordeaux-700 leading-none">MG</span>
                  </div>
                </div>
              </div>

              <div className="px-8 pt-16 pb-8 sm:px-10 sm:pt-20 sm:pb-10">
                <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-bordeaux-700">Coach em PNL</div>
                <h3 className="mt-2 font-display text-3xl sm:text-4xl text-stone-900 leading-tight">Madalena Guedes</h3>
                <p className="mt-4 text-stone-600 leading-relaxed text-sm sm:text-base">
                  Coach certificada em Programação Neurolinguística, lidera as sessões de coaching e
                  desenvolvimento pessoal do iLg — ajudando cada aluno a desbloquear o seu potencial.
                </p>

                <div className="mt-6 flex flex-wrap gap-3">
                  <a
                    href={coachUrls.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="coach-linkedin"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-bordeaux-700 text-white text-sm font-semibold hover:bg-bordeaux-800 transition-all hover:-translate-y-0.5 shadow-[0_8px_24px_rgba(124,31,44,0.18)]"
                  >
                    <Linkedin size={16} /> LinkedIn
                  </a>
                  <a
                    href={coachUrls.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="coach-instagram"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-stone-300 text-stone-800 text-sm font-semibold hover:border-bordeaux-700 hover:text-bordeaux-700 transition-colors"
                  >
                    <Instagram size={16} /> Instagram
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
